import { ulid } from 'ulid';
import { ActionError, ActionPayloads, applyPrimitives, changesOf, expandAction, expectedOf, summarize, type Action, type ActionPayload, type ActionResult, type ActionType, type Contact, type LogEntry, type Replica, type Synced, type User } from '@crmsy/shared';
import { todayIso } from './today.js';
import { ApiError, postAction } from './api.js';
import { db, getCachedUser, provisionalLogId, type OutboxItem } from './db.js';
import { syncClient } from './sync.js';
import { toast } from './toast.js';
import { t } from '../i18n/index.js';

/**
 * Client write path (§5.4): build the action, apply it to IndexedDB at once (optimistic),
 * queue it in `outbox`, and let the worker post actions in order. The provisional log
 * row carries `pending: true` until the pull replaces it with the server's row.
 */

export async function dispatch<T extends ActionType>(type: T, payload: ActionPayload<T>): Promise<string> {
  const me = await getCachedUser();
  if (!me) throw new Error('not signed in');
  const parsed = ActionPayloads[type].parse(payload) as ActionPayload<T>;
  const actionId = ulid();
  const at = new Date().toISOString();
  const action = { actionId, at, type, payload: parsed, expected: {} } as unknown as Action;

  await db.transaction('rw', [db.contacts, db.log, db.outbox], async () => {
    const contactId = targetOf(action);
    const entry = action.type === 'revert' ? ((await db.log.get(action.payload.entryId)) ?? null) : null;
    if (action.type === 'revert' && !entry) throw new ActionError('log entry not found', 404);
    const cid = action.type === 'revert' ? entry!.contactId : contactId;
    const contact = cid ? ((await db.contacts.get(cid)) ?? null) : null;

    const expansion = expandAction(action, { contact, entry, today: todayIso(), me: { notionUserId: me.notionUserId } });
    action.expected = expectedOf(expansion.primitives);

    // Apply primitives to a one-contact replica, then write the result back.
    const replica: Replica = { contacts: new Map(), log: new Map() };
    if (contact) replica.contacts.set(contact.id, contact);
    const logIds = expansion.primitives.flatMap((p) => (p.kind === 'archiveLog' || p.kind === 'restoreLog' ? [p.entry] : []));
    const logRows = logIds.length ? await db.log.bulkGet(logIds) : [];
    for (const row of logRows) if (row) replica.log.set(row.id, row);
    applyPrimitives(replica, expansion.primitives, at);

    const rev = contact?.rev ?? 0;
    for (const c of replica.contacts.values()) await db.contacts.put({ ...c, rev });
    for (const e of replica.log.values()) await db.log.put({ ...e, rev: (e as Synced<LogEntry>).rev ?? 0 });

    const provisional: Synced<LogEntry> = {
      id: provisionalLogId(actionId),
      name: '',
      contactId: expansion.entry.contactId,
      type: expansion.entry.type,
      actor: me.notionUserId,
      actorEmail: me.email,
      at,
      text: expansion.entry.text,
      changes: changesOf(expansion.primitives),
      actionId,
      reverts: expansion.entry.reverts,
      reverted: false,
      source: 'app',
      conflict: false,
      origin: expansion.entry.origin ?? null,
      archived: false,
      lastEditedTime: at,
      pending: true,
      rev: 0,
    };
    provisional.name = `${me.name} · ${summarize(provisional)}`;
    await db.log.put(provisional);
    if (entry) await db.log.put({ ...entry, reverted: true });

    const item: OutboxItem = {
      actionId,
      type,
      payload: parsed,
      expected: action.expected,
      at,
      status: 'queued',
      attempts: 0,
      contactId: expansion.entry.contactId,
      snapshot: contact,
      logSnapshot: [
        ...logRows.filter((r): r is Synced<LogEntry> => !!r).map((r) => ({ id: r.id, archived: r.archived })),
        ...(entry ? [{ id: entry.id, archived: entry.archived, reverted: entry.reverted }] : []),
      ],
    };
    await db.outbox.put(item);
  });
  void worker.kick();
  return actionId;
}

function targetOf(a: Action): string | null {
  if (a.type === 'createProspect' || a.type === 'revert') return null;
  return a.payload.contactId;
}

/* ---------- outbox worker ---------- */

class OutboxWorker {
  private running = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private backoff = 2000;

  constructor() {
    window.addEventListener('online', () => void this.kick());
  }

  /** Post queued actions in order until the queue is empty or one must wait. */
  async kick(): Promise<void> {
    if (this.running) return;
    this.running = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    try {
      // Resume anything left in `sending` by a closed tab.
      await db.outbox.where('status').equals('sending').modify({ status: 'queued' });
      for (;;) {
        const next = (await db.outbox.where('status').equals('queued').sortBy('actionId'))[0];
        if (!next) break;
        if (!navigator.onLine) {
          this.schedule();
          break;
        }
        await db.outbox.update(next.actionId, { status: 'sending', attempts: next.attempts + 1 });
        let result: ActionResult;
        try {
          result = await postAction({ actionId: next.actionId, at: next.at, type: next.type, payload: next.payload, expected: next.expected });
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            await db.outbox.update(next.actionId, { status: 'queued' });
            syncClient.onUnauthenticated();
            break;
          }
          if (e instanceof ApiError && !e.retry) {
            await this.fail(next, e.message);
            continue;
          }
          // Network error or 503: stay queued, try again later (§5.4 step 2).
          await db.outbox.update(next.actionId, { status: 'queued' });
          this.schedule();
          break;
        }
        this.backoff = 2000;
        await this.succeed(next, result);
      }
    } finally {
      this.running = false;
    }
  }

  private schedule() {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.kick();
    }, this.backoff);
    this.backoff = Math.min(this.backoff * 2, 60_000);
  }

  private async succeed(item: OutboxItem, result: ActionResult) {
    await db.transaction('rw', [db.contacts, db.log, db.outbox], async () => {
      await db.outbox.delete(item.actionId);
      for (const [tempId, realId] of Object.entries(result.mapping)) await renameContact(tempId, realId);
    });
    // Tell views after the commit, so a panel open on the temp id switches once the real row is readable.
    for (const [tempId, realId] of Object.entries(result.mapping)) for (const l of renameListeners) l(tempId, realId);
    if (result.conflict) toast(t('toast.conflict'), { kind: 'info', ttl: 8000 });
    await syncClient.pull();
  }

  /** 4xx: roll the optimistic change back (§5.4 step 2) and offer retry / discard. */
  private async fail(item: OutboxItem, error: string) {
    await db.transaction('rw', [db.contacts, db.log, db.outbox], async () => {
      await db.outbox.update(item.actionId, { status: 'failed', error });
      await rollback(item);
    });
    toast(t('toast.couldNotSave', { error }), {
      kind: 'error',
      actions: [
        { label: t('toast.retry'), run: () => void this.retry(item.actionId) },
        { label: t('toast.discard'), run: () => void this.discard(item.actionId) },
      ],
    });
  }

  async retry(actionId: string): Promise<void> {
    const item = await db.outbox.get(actionId);
    if (!item) return;
    // Re-apply optimistically from the stored payload, then queue again.
    await db.outbox.delete(actionId);
    try {
      await dispatch(item.type as ActionType, item.payload as never);
    } catch (e) {
      toast(t('toast.couldNotRetry', { error: e instanceof Error ? e.message : String(e) }), { kind: 'error' });
    }
  }

  async discard(actionId: string): Promise<void> {
    await db.outbox.delete(actionId);
  }
}

async function rollback(item: OutboxItem) {
  if (item.contactId) {
    const current = await db.contacts.get(item.contactId);
    if (item.snapshot) {
      // Only restore if no authoritative row arrived meanwhile.
      if (!current || current.rev === item.snapshot.rev) await db.contacts.put(item.snapshot);
    } else if (current && current.rev === 0) {
      await db.contacts.delete(item.contactId); // the temp contact of a failed create
    }
  }
  await db.log.delete(provisionalLogId(item.actionId));
  for (const snap of item.logSnapshot) {
    const row = await db.log.get(snap.id);
    if (row) await db.log.put({ ...row, archived: snap.archived, ...(snap.reverted !== undefined ? { reverted: snap.reverted } : {}) });
  }
}

/** A created contact gets its Notion id: rewrite the replica and anything queued that points at the temp id. */
async function renameContact(tempId: string, realId: string) {
  const temp = await db.contacts.get(tempId);
  if (temp) {
    await db.contacts.delete(tempId);
    if (!(await db.contacts.get(realId))) await db.contacts.put({ ...temp, id: realId });
  }
  await db.log.where('contactId').equals(tempId).modify({ contactId: realId });
  await db.outbox.where('contactId').equals(tempId).modify((o: OutboxItem) => {
    o.contactId = realId;
    const p = o.payload as { contactId?: string };
    if (p && p.contactId === tempId) p.contactId = realId;
  });
}

const renameListeners = new Set<(tempId: string, realId: string) => void>();
/** Views holding a temp id (selection, open panel) switch to the real one. */
export function onContactRenamed(fn: (tempId: string, realId: string) => void): () => void {
  renameListeners.add(fn);
  return () => {
    renameListeners.delete(fn);
  };
}

export const worker = new OutboxWorker();

/* ---------- helpers for views ---------- */

export const retryAction = (actionId: string) => worker.retry(actionId);
export const discardAction = (actionId: string) => worker.discard(actionId);

/** The latest entry of mine that can still be reverted (`u`, §7). */
export async function myLastRevertable(me: User): Promise<LogEntry | null> {
  const mine = await db.log.filter((e) => e.actorEmail?.toLowerCase() === me.email.toLowerCase() && !e.reverted && !e.archived && !e.pending && e.type !== 'suggestion').toArray();
  mine.sort((a, b) => b.at.localeCompare(a.at));
  return mine[0] ?? null;
}

export function isTempId(id: string): boolean {
  return /^[0-9A-HJKMNP-TV-Z]{26}$/.test(id);
}

export type { Contact };
